import { useEffect, useRef, useState } from "react";
import { Button, Flex, Modal, message, Table, Input, Spin } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  fetchCategory,
  postCategory,
  putCategory,
  deleteCategory,
} from "../Service/category";
import CategoryForm from "./categoryForm";

const Category = () => {
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("category");
  const [sortOrder, setSortOrder] = useState("ascend");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const searchTimer = useRef(null);

  // ================= FETCH =================
  const fetchData = async (
    pageNum = page,
    pageSize = size,
    searchText = search,
    field = sortField,
    order = sortOrder
  ) => {
    setLoading(true);
    try {
      const res = await fetchCategory({
        page: pageNum - 1,
        size: pageSize,
        search: searchText || undefined,
        sort: field,
        desc: order === "descend",
      });

      setCategories(res.data?.content || []);
      setTotal(res.data?.totalElements || 0);
      setPage(pageNum);
      setSize(pageSize);
      setSortField(field);
      setSortOrder(order);
    } catch {
      message.error("Network error while fetching categories");
    } finally {
      setLoading(false);
    }
  };

  // ================= INIT LOAD =================
  useEffect(() => {
    fetchData();
  }, []);

  // ================= DEBOUNCED SEARCH =================
  useEffect(() => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }

    searchTimer.current = setTimeout(() => {
      fetchData(1, size, search);
    }, 500);

    return () => clearTimeout(searchTimer.current);
  }, [search]);

  // ================= SAVE =================
  const saveData = async (data) => {
    setLoading(true);
    try {
      if (editData) {
        await putCategory(data, editData.id);
      } else {
        await postCategory(data);
      }

      messageApi.success(
        `Category ${editData ? "updated" : "added"} successfully`
      );
      handleCancel();
      fetchData();
      return true;
    } catch {
      messageApi.error("Error while saving category");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ================= DELETE =================
  const deleteData = async (id) => {
    try {
      await deleteCategory(id);
      messageApi.success("Category deleted successfully");
      fetchData();
    } catch {
      messageApi.error("Error while deleting category");
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditData(null);
  };

  // ================= COLUMNS =================
  const columns = [
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      sorter: true,
      sortOrder: sortField === "category" ? sortOrder : null,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <>
          <EditOutlined
            style={{ marginRight: 12, cursor: "pointer" }}
            onClick={() => {
              setEditData(record);
              setIsModalOpen(true);
            }}
          />
          <DeleteOutlined
            style={{ cursor: "pointer" }}
            onClick={() => deleteData(record.id)}
          />
        </>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div style={{ width: "100%" }}>
        {contextHolder}

        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: 16 }}
        >
          <div style={{ fontSize: "1rem", fontWeight: 700 }}>Category</div>
          <Flex gap={8}>
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
            <Button type="primary" onClick={() => setIsModalOpen(true)}>
              Add
            </Button>
          </Flex>
        </Flex>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={categories}
          bordered
          pagination={{
            current: page,
            pageSize: size,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          onChange={(pagination, filters, sorter) => {
            fetchData(
              pagination.current,
              pagination.pageSize,
              search,
              sorter.field || sortField,
              sorter.order || sortOrder
            );
          }}
        />

        <Modal
          title="Category Form"
          centered
          open={isModalOpen}
          footer={false}
          onCancel={handleCancel}
          destroyOnClose
        >
          <CategoryForm editData={editData} saveData={saveData} />
        </Modal>
      </div>
    </Spin>
  );
};

export default Category;
